import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItem from '@material-ui/core/ListItem';
import SvgIcon from '@material-ui/core/SvgIcon';
import PlayIcon from '@material-ui/icons/PlayCircleFilled';
import TrendingIcon from '@material-ui/icons/TrendingUp';
import LaunchIcon from '@material-ui/icons/Launch';
import { HEADER_HEIGHT } from '../../base/constants';
import { checkMobileOperatingSystem } from '../utils/checkMobileOperatingSystem';
import { AssetsFileResolver } from '../containers/AssetsFileResolver';
import { externalUrlsFactory } from '../constants/externalUrls';
import { LeftSection } from './Header/LeftSection';

const PADDING_LEFT = '48px !important';
const tvIcon = (
  <path
    d="M19.2327759,20.2749555 C19.4440849,20.2749555 19.6153846,20.4462552 19.6153846,20.6575642 L19.6153846,21.6173913 C19.6153846,21.8287003 19.4440849,22 19.2327759,22 L4.76722408,22 C4.55591513,22 4.38461538,21.8287003 4.38461538,21.6173913 L4.38461538,20.6575642 C4.38461538,20.4462552 4.55591513,20.2749555 4.76722408,20.2749555 L19.2327759,20.2749555 Z M22.4887411,5 C22.8122276,5 23.0278852,5.25283821 22.997077,5.56186269 L22.997077,5.56186269 L22.997077,18.2178198 C22.997077,18.5268443 22.7968235,18.7656359 22.4579329,18.7656359 L22.4579329,18.7656359 L1.66237697,18.7796825 C1.33889054,18.7796825 1,18.5408908 1,18.2318663 L1,18.2318663 L1,5.56186269 C1,5.25283821 1.32348643,5 1.66237697,5 L1.66237697,5 Z"
    id="Combined-Shape"
  />
);

const StyledDrawer = styled((props) => <Drawer classes={{ paper: 'paper' }} {...props} />)`
  font-family: 'Pluto Sans Bold';
  & .paper {
    background: #151515;
    width: 360px;

    @media (max-width: 768px) {
      width: 100%;
      margin-bottom: 50px;
    }
  }
`;

const Header = styled.div`
  font-size: 20px;
  height: ${HEADER_HEIGHT}px;
  display: flex;
  // prevent container collapse on iOS
  flex-shrink: 0;
`;

const AppStoreBadge = styled(Button).attrs({ component: 'a', target: '_blank' })`
  margin-right: ${({ isEU }) => (isEU ? '2px' : '15px')};
  margin-top: ${({ isEU }) => (isEU ? '70px' : '10px')};
  ${({ isEU }) => (isEU ? 'flex: auto;' : '')}
  ${({ isEU }) => (isEU ? 'z-index: 10;' : '')}
  span {
    ${({ isEU }) => (isEU ? 'justify-content: flex-end;' : '')}
  }
  img {
    width: 120px;
    height: auto;
    vertical-align: top;
  }
`;

const GooglePlayButton = styled(Button).attrs({ component: 'a', target: '_blank' })`
  margin-right: ${({ isEU }) => (isEU ? '2px' : '15px')};
  margin-top: ${({ isEU }) => (isEU ? '70px' : '10px')};
  ${({ isEU }) => (isEU ? 'flex: auto;' : '')}
  ${({ isEU }) => (isEU ? 'z-index: 10;' : '')}
  span {
    ${({ isEU }) => (isEU ? 'justify-content: flex-end;' : '')}
  }
  img {
    width: 120px;
    height: auto;
    vertical-align: top;
  }
`;

const StyledListSubheader = styled(ListSubheader)`
  font-family: 'Pluto Sans Bold';
  font-size: 14px;
  font-weight: bold !important;
  color: #a8a8a8 !important;
  text-transform: uppercase;
  padding-left: ${PADDING_LEFT};
  margin-top: 0.5em;

  & {
    position: relative !important;
  }
`;

const ListItemLink = styled(({ external = false, children, url, isDesktopApp, ...other }) => {
  // if desktop app, execute href with click handler to fix window
  // not opening in the app
  if (isDesktopApp) {
    return (
      <ListItem button onClick={() => window.open(url)} {...other}>
        {children}
        {external && <LaunchIcon className="externalIcon" />}
      </ListItem>
    );
  } else {
    return (
      <ListItem
        button
        component="a"
        target={external ? '_blank' : '_self'}
        href={url}
        disableripple="true"
        {...other}
      >
        {children}
        {external && <LaunchIcon className="externalIcon" />}
      </ListItem>
    );
  }
})`
  font-size: 15px !important;
  font-weight: bold;
  padding-left: ${PADDING_LEFT};
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  color: #e7e7e7 !important;
  &:hover {
    background: #1f1f1f !important;
    .externalIcon {
      visibility: visible;
    }
  }
  .externalIcon {
    position: absolute;
    right: 24px;
    visibility: hidden;
  }
`;

const Copyright = styled.div`
  font-size: 14px;
  font-weight: bold;
  padding-left: ${PADDING_LEFT};
  color: #626262;
  margin-top: 2em;
`;

const BASE_URL = '//pluto.tv';

const styles = {
  icon: {
    verticalAlign: 'sub',
    marginRight: '5px',
  },
};

class Menu extends PureComponent {
  constructor(props) {
    super(props);
    const { isIos, isAndroid } = checkMobileOperatingSystem();
    this.isOs = isIos;
    this.isAndroid = isAndroid;
  }

  redirect = (e, action) => {
    e.preventDefault();
    action();
    this.props.closeMenu();
  };

  redirectToVod = (e) => this.redirect(e, this.props.redirectToVod);
  redirectToWatch = (e) => this.redirect(e, this.props.redirectToWatch);
  redirectToTrending = (e) => this.redirect(e, this.props.redirectToTrending);

  render() {
    const { classes, isMenuOpen, closeMenu, t, trendingVisible, marketingRegion } = this.props;

    const currentYear = new Date().getFullYear();
    const externalUrls = externalUrlsFactory(marketingRegion, 'hambuerger-menu');
    const userAgent = window.navigator.userAgent;
    const isDesktopApp = userAgent.match(/\PlutoTV\b.*\Electron\b/g);
    const isLatamRegion = marketingRegion === 'latam';
    const isEURegion = ['gsa', 'uk'].indexOf(marketingRegion) !== -1;
    // Highlighting Spain in EU specifically to be able to make "Support" link visible for users in Spain
    // when Support link is hidden for the rest of EU
    // https://plutotv.atlassian.net/browse/WWW-1569
    const isSpain = marketingRegion === 'es';

    return (
      <StyledDrawer open={isMenuOpen} onClose={closeMenu}>
        <Header>
          <LeftSection />
          {this.isOs && (
            <AppStoreBadge
              href="https://apps.apple.com/us/app/pluto-tv-live-tv-and-movies/id751712884"
              isEU={isEURegion || isSpain}
            >
              <AssetsFileResolver
                path={isLatamRegion ? 'images/es/AppStore.png' : 'images/app_store_badge.svg'}
              />
            </AppStoreBadge>
          )}
          {this.isAndroid && (
            <GooglePlayButton
              href="https://play.google.com/store/apps/details?id=tv.pluto.android"
              isEU={isEURegion || isSpain}
            >
              <AssetsFileResolver
                path={isLatamRegion ? 'images/es/GooglePlay.png' : 'images/GooglePlay.png'}
              />
            </GooglePlayButton>
          )}
        </Header>
        <List>
          <StyledListSubheader>{t('watch_now')}</StyledListSubheader>
          <ListItemLink href="/live-tv" external={false} onClick={this.redirectToWatch}>
            <SvgIcon className={classes.icon}>{tvIcon}</SvgIcon> {t('live_tv')}
          </ListItemLink>
          <ListItemLink href="/on-demand" external={false} onClick={this.redirectToVod}>
            <PlayIcon className={classes.icon} /> {t('on_demand')}
          </ListItemLink>
          {trendingVisible && (
            <ListItemLink href="/trending" external={false} onClick={this.redirectToTrending}>
              <TrendingIcon className={classes.icon} /> {t('trending')}
            </ListItemLink>
          )}

          {<StyledListSubheader>{t('more_about_pluto_tv_caps')}</StyledListSubheader>}

          {externalUrls.about.map(({ customTitle, titleKey, url }) => (
            <ListItemLink key={titleKey} external url={url} isDesktopApp={isDesktopApp}>
              {customTitle || t(titleKey)}
            </ListItemLink>
          ))}

          <StyledListSubheader>{t('legal_caps')}</StyledListSubheader>
          {externalUrls.legal.map(({ customTitle, titleKey, url }) => (
            <ListItemLink key={titleKey} external url={url} isDesktopApp={isDesktopApp}>
              {customTitle || t(titleKey)}
            </ListItemLink>
          ))}
        </List>

        <Copyright>© {currentYear} Pluto Inc. {t('all_rights_reserved')}</Copyright>
      </StyledDrawer>
    );
  }
}

Menu.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isMenuOpen: PropTypes.bool.isRequired,
  openMenu: PropTypes.func.isRequired,
  closeMenu: PropTypes.func.isRequired,
  redirectToWatch: PropTypes.func.isRequired,
  redirectToVod: PropTypes.func.isRequired,
  redirectToTrending: PropTypes.func.isRequired,
  trendingVisible: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  marketingRegion: PropTypes.string,
};

Menu = withStyles(styles)(withNamespaces()(Menu));

export { Menu };
